import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectAllNodes, selectAllPoints } from '../map/slice/mapSelectors';
import { BelowMapSetter } from '../layouts/BelowMapContext';
import { BelowMapTab } from '../layouts/BelowMapTab';
import {
  EyeIcon,
  EyeOffIcon,
  FilterIcon,
  ZoomInIcon,
} from '@iconicicons/react';
import { plotterActions } from '../plotter/plotterSlice';
import { useTspFilter } from '../helpers/hooks';
import { generateColor } from '../utils/colors';
import { useFitMap } from '../plotter/usePanMap';
import { LoadingIndicator } from '../helpers/LoadingIndicator';
import {
  selectRiseChainsLoading,
  selectAllRiseChains,
  selectShowRiseChains,
} from '../plotter/plotterSelectors';
import { Node, Point } from '@/types';
import { priceFormatter } from '../helpers/formatters';
import { IconButton } from '../helpers/IconButton';
import { Circle } from '../helpers/Circle';

export const RiseTransportTable = () => {
  const dispatch = useDispatch();
  const setTspFilter = useTspFilter();

  const fitMap = useFitMap();

  const allNodes = useSelector(selectAllNodes);

  const points = useSelector(selectAllPoints);

  const riseChains = useSelector(selectAllRiseChains);
  const riseChainsLoading = useSelector(selectRiseChainsLoading);

  const showRiseChains = useSelector(selectShowRiseChains);

  const zoomToPath = (nodes: Node[]) => {
    if (nodes.length === 0) {
      return;
    }

    fitMap(nodes);
  };

  const filterTsps = (points: Point[]) => {
    if (points.length === 0) {
      return;
    }
    setTspFilter(points.map((point) => point.tsp_id));
  };

  const allHidden = riseChains.every((chain) => !chain.visible);

  if (!showRiseChains) {
    return null;
  }
  return (
    <BelowMapSetter>
      <BelowMapTab
        key={'rise-transports-table'}
        tabName="Rise Transports"
        contextKey={'rise-transports-table'}
        tabOrder={-1}
      >
        <table className="table">
          <thead>
            <tr>
              <th>
                {allHidden ? (
                  <EyeOffIcon
                    role="button"
                    onClick={() =>
                      dispatch(
                        plotterActions.setRiseChainsVisibility({
                          visible: true,
                        }),
                      )
                    }
                  />
                ) : (
                  <EyeIcon
                    role="button"
                    onClick={() =>
                      dispatch(
                        plotterActions.setRiseChainsVisibility({
                          visible: false,
                        }),
                      )
                    }
                  />
                )}
              </th>
              <th>Chain</th>
              <th>Description</th>
              <th>Receipt Point</th>
              <th>Receipt Trade</th>
              <th>Receipt Price</th>
              <th>Delivery Point</th>
              <th>Delivery Trade</th>
              <th>Delivery Price</th>
              <th>Contract</th>
              <th>Transport Cost</th>
              <th>Delivery Volume</th>
              <th>Profit</th>
            </tr>
          </thead>
          <tbody>
            {riseChainsLoading ? (
              <tr>
                <td colSpan={100}>
                  <LoadingIndicator className="my-3" />
                </td>
              </tr>
            ) : riseChains.length > 0 ? (
              riseChains.map((chain) => {
                const sortedOperations = chain.operation_links
                  .slice()
                  .sort((a, b) => a.link_order - b.link_order);
                const receiptOperation = sortedOperations[0];
                const deliveryOperation =
                  sortedOperations[sortedOperations.length - 1];
                const receiptPoint =
                  points[receiptOperation.operation_receipt_point_id];
                const deliveryPoint =
                  points[deliveryOperation.operation_delivery_point_id];
                const toggleVisibility = () => {
                  dispatch(
                    plotterActions.setRiseChainVisibility({
                      chainId: chain.chain_id,
                      visible: !chain.visible,
                    }),
                  );
                };
                const pathNodes = chain.operation_links.flatMap((operation) =>
                  operation.operation_path
                    .map((nodeId) => allNodes[nodeId])
                    .filter(Boolean),
                );
                const pathPoints = chain.operation_links
                  .flatMap((operation) =>
                    operation.operation_path
                      .map((nodeId) => allNodes[nodeId]?.points[0])
                      .filter(Boolean)
                      .map((pointId) => points[pointId])
                      .filter(Boolean),
                  )
                  .filter(Boolean);
                const hasNodes = pathNodes.length >= 2;
                const hasPoints = pathPoints.length >= 1;
                return (
                  <tr key={chain.chain_id}>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <span className="d-flex gap-2 mx-1">
                        <IconButton
                          icon={chain.visible ? EyeIcon : EyeOffIcon}
                          onClick={toggleVisibility}
                          disabled={!hasNodes}
                        />
                        <IconButton
                          icon={ZoomInIcon}
                          // className="ms-2"
                          onClick={() => zoomToPath(pathNodes)}
                          disabled={!hasNodes}
                        />
                        <IconButton
                          // className="ms-2"
                          icon={FilterIcon}
                          onClick={() => filterTsps(pathPoints)}
                          disabled={!hasPoints}
                        />
                      </span>
                    </td>
                    <td>
                      <Circle
                        color={generateColor(chain.chain_id, 80, 70)}
                        className="me-2"
                      />
                      {chain.chain_id}
                    </td>
                    <td style={{ maxWidth: '400px' }}>{chain.chain_callout}</td>
                    <td>{receiptPoint?.name}</td>
                    <td style={{ maxWidth: '300px' }}>
                      {chain.chain_receipt_trade_description}
                    </td>
                    <td>
                      {chain.chain_receipt_price === null
                        ? '-'
                        : priceFormatter.format(
                            Number(chain.chain_receipt_price),
                          )}
                    </td>
                    <td>{deliveryPoint?.name}</td>
                    <td style={{ maxWidth: '300px' }}>
                      {chain.chain_delivery_trade_description}
                    </td>
                    <td>
                      {chain.chain_delivery_price === null
                        ? '-'
                        : priceFormatter.format(
                            Number(chain.chain_delivery_price),
                          )}
                    </td>
                    <td>
                      {chain.operation_links
                        .map((t) => t.operation_contract_num)
                        .filter(Boolean)
                        .join(', ')}
                    </td>
                    <td>
                      {chain.chain_transport_cost === null
                        ? '-'
                        : priceFormatter.format(
                            Number(chain.chain_transport_cost),
                          )}
                    </td>
                    <td>{chain.chain_delivered_dth}</td>
                    <td>
                      {chain.chain_profit === null
                        ? '-'
                        : priceFormatter.format(Number(chain.chain_profit))}
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={100}>
                  <p className="text-center my-3">No Rise Transports</p>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </BelowMapTab>
    </BelowMapSetter>
  );
};
